import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import b from 'b_';
import PropTypes from 'prop-types';
import AuthenticationConfigurationStore
    from '../../stores/AuthenticationConfigurationSSO/AuthenticationConfigurationStore';
import AuthenticationConfEditStore from '../../stores/AuthenticationConfigurationSSO/AuthenticationConfEditStore';
import ConsoleStore from '../../stores/ConsoleStore';
import { ConsoleCard, FuseConsoleField, FuseConsoleCardFooter } from '../ConsoleFragments';
import FuseConsoleModal from '../ConsoleModal/FuseConsoleModal';
import { FTHeading, FTBody } from '../Fuse/Text';
import FuseSwitch from '../FuseControls/FuseSwitch';
import FuseButton from '../FuseControls/FuseButton';
import FuseTextfield from '../FuseControls/FuseTextfield';
import FuseTextArea from '../FuseControls/FuseTextArea';
import FuseRadioButton from '../FuseControls/FuseRadioButton';
import { ObservableFuseSelect } from '../FuseControls/FuseSelect';
import SnackStore from '../../stores/SnackStore';
import copyText from '../../copyText';
import FuseAlert from '../FuseControls/FuseAlert';
import UDSpinner from '../UD/Fragments/UDSpinner';
import debug, { DEBUG } from '../../debug';

import './EditConfigurationModal.css';

const block = b.lock('EditConfigurationModal');

const logger = debug.create('EditConfigurationModal', DEBUG);

class EditConfigurationModal extends Component {
    static propTypes = {
        useSSOLogin: PropTypes.bool,
        authenticationConfiguration: AuthenticationConfigurationStore.PropType.isRequired,
        console: ConsoleStore.PropType.isRequired,
        snack: SnackStore.PropType.isRequired,
    };

    onEditModalSave = () => {
        const { authenticationConfiguration } = this.props;
        if (authenticationConfiguration.editStore.hasInvalidFields()) {
            authenticationConfiguration.editStore.setErrorMessages();
            this.props.snack.showError('There is an error with one or more entries.');
        } else {
            authenticationConfiguration.editStore.clearErrorMessages();
            authenticationConfiguration.onEditModalSave().then(() => {
                authenticationConfiguration.editModal.close();
                this.props.snack.showSuccess('SAML Authentication has been saved.');
            }).catch((error) => {
                let msg = 'There is an error with one or more entries.';
                if (error.message.includes('saml_config.certificate')) {
                    authenticationConfiguration.editStore.X_Certificate.errorMessage =
                        AuthenticationConfEditStore.INVALID_INPUT;
                }
                if (error.message.includes('saml_config.sso_url')) {
                    authenticationConfiguration.editStore.idProviderURL.errorMessage =
                        AuthenticationConfEditStore.INVALID_INPUT;
                }
                if (error.message.includes('already has assume ownership enabled')) {
                    msg = 'Assume ownership is already in use by another IdP';
                }
                if (/saml issuer.+already exists/.test(error.message)) {
                    msg = 'IdP saml issuer already exists';
                }
                this.props.snack.showError(msg);
            });
        }
    };

    generateKey = () => {
        const { authenticationConfiguration } = this.props;
        authenticationConfiguration.onCreateKeyClick().then(() => {
            this.props.snack.showSuccess('SCIM API key has been generated.');
        }).catch(() => {
            this.props.snack.showError(AuthenticationConfigurationStore.GENERIC_FAILURE_MSG);
        });
    };

    copyToClipboard = (text, itemName) => {
        if (copyText(text)) {
            this.props.snack.showSuccess(`${itemName} has been copied to clipboard`);
        } else {
            this.props.snack.showError('Unable to copy');
        }
    };

    isSaveButtonDisabled() {
        const { authenticationConfiguration } = this.props;
        if (authenticationConfiguration.editStore.hasSwitchedToDisable()) {
            return false;
        }
        if (authenticationConfiguration.editStore.isSCIM
            && !authenticationConfiguration.hasKey
            && authenticationConfiguration.editStore.isOwnIDP) {
            return true;
        }
        if (!authenticationConfiguration.editStore.isValid()) {
            return true;
        }
        if (authenticationConfiguration.editStore.isJIT || authenticationConfiguration.hasExistingAccount) {
            return !authenticationConfiguration.editStore.hasChanged();
        }
        return !authenticationConfiguration.editStore.hasChanged();
    }

    onStatusEnable = (newVal) => {
        const { authenticationConfiguration: confStore } = this.props;
        if (!this.props.console.isSSOConfigured && newVal) {
            logger.info('onStatusEnable');
            confStore.enableTenant().then((isSuccess) => {
                if (isSuccess) {
                    this.props.console.setSSOStatusDisabled();
                    logger.info('setSSOStatusDisabled');
                }
            });
        }
    };

    renderSCIM() {
        const { authenticationConfiguration } = this.props;
        if (authenticationConfiguration.editStore.isSCIM) {
            return <Fragment>
                <div className={block('SCIM_Wrapper')}>
                    <FuseTextfield
                        store={authenticationConfiguration.apiKey}
                        label="SCIM API Key (required)"
                        leftIcon={'key'}
                        className={block('SCIM_key')}
                        disabled
                    />
                    {authenticationConfiguration.hasKey ? <Fragment>
                        <FuseButton
                            className={block('SCIM_CreateKey')}
                            icon="delete"
                            type={'tertiary'}
                            color={'red'}
                            onClick={authenticationConfiguration.onDeleteKeyClick}
                        ><FTBody className={block('UrlsButtonText', { red: true })}>
                            Delete SCIM API Key
                            </FTBody></FuseButton>
                        <FuseButton
                            className={block('UrlsButton')}
                            icon={'assignment'}
                            type={'tertiary'}
                            color={'blue'}
                            onClick={() => this.copyToClipboard(
                                authenticationConfiguration.apiKey.value, 'SCIM Key')}
                        ><FTBody className={block('UrlsButtonText')}>Copy to clipboard</FTBody>
                        </FuseButton>
                    </Fragment> : <FuseButton
                        className={block('SCIM_CreateKey')}
                        icon="add_circle"
                        type={'tertiary'}
                        color={'blue'}
                        loading={authenticationConfiguration.generateKeyStatus.isLoading}
                        onClick={this.generateKey}
                    ><FTBody className={block('UrlsButtonText')}>Generate SCIM API Key</FTBody></FuseButton>}
                </div>
                {authenticationConfiguration.hasKey ?
                    <Fragment>
                        <div className={block('URLsField')}>
                            <FuseTextfield
                                store={authenticationConfiguration.tenantURL}
                                label="Base/Tenant URL (required)"
                                className={block('SCIM_url')}
                                disabled
                            />
                            {authenticationConfiguration.tenantURL.value.startsWith('https') &&
                            <FuseButton
                                type={'tertiary'}
                                color={'blue'}
                                icon={'assignment'}
                                className={block('UrlsButton')}
                                onClick={() => this.copyToClipboard(
                                    authenticationConfiguration.tenantURL.value, 'Base/Tenant URL')}
                            > <FTBody className={block('UrlsButtonText')}>Copy to clipboard</FTBody> </FuseButton>}
                        </div>
                    </Fragment> : null
                }
            </Fragment>;
        }
        return null;
    }

    renderLDAP(editStore) {
        const isShowDummyPassword = editStore.isAlreadyConfiguredIDP
            && !editStore.isEditPasswordMode.isTrue
            && editStore.password.value === '';
        if (editStore.isLDAP) {
            return <Fragment>
                <FuseTextfield
                    store={editStore.ldapURL}
                    label="LDAP URL (required)"
                    placeholder="Enter LDAP URL (ldap(s)://ldap.com)"
                    className={block('Field', { type: 'text' })}
                />
                <FuseTextfield
                    store={editStore.userName}
                    label="Username (required)"
                    placeholder="Enter username"
                    className={block('Field', { type: 'text' })}
                />
                {isShowDummyPassword ?
                    <FuseTextfield
                        store={editStore.dummyPassword}
                        onFocus={() => editStore.isEditPasswordMode.setTrue()}
                        onBlur={() => editStore.isEditPasswordMode.setFalse()}
                        label="Password (required)"
                        placeholder="Enter password"
                        type={'password'}
                        className={block('Field', { type: 'text' })}
                    /> : <FuseTextfield
                        store={editStore.password}
                        onBlur={() => editStore.isEditPasswordMode.setFalse()}
                        label="Password (required)"
                        placeholder="Enter password"
                        className={block('Field', { type: 'text' })}
                    />}
                <FuseTextfield
                    store={editStore.userBaseDN}
                    label="User Base DN (required)"
                    placeholder="Enter user base DN"
                    className={block('Field', { type: 'text' })}
                />
                <FuseTextfield
                    store={editStore.groupsBaseDN}
                    label="Groups Base DN"
                    placeholder="Enter group base DN"
                    className={block('Field', { type: 'text' })}
                />
                <FuseTextfield
                    store={editStore.usersFilter}
                    label="Users Filter"
                    placeholder="For example: (cn=joe*)(mail=John@doe.com))"
                    className={block('Field', { type: 'text' })}
                />
                <FuseTextfield
                    store={editStore.groupsFilter}
                    label="Groups Filter"
                    placeholder="For example: (cn=joe*)(mail=John@doe.com))"
                    className={block('Field', { type: 'text' })}
                />
                <FuseTextArea
                    store={editStore.LDAP_Certificate}
                    label="Certificate (PEM)"
                    placeholder="Paste certificate"
                    className={block('Field', { type: 'textarea' })}
                />
            </Fragment>;
        }
        return null;
    }

    renderLDAPWarning = (editStore) => {
        if (editStore.isLDAP) {
            return <FuseAlert
                title="WARNING -"
                subtitle="To use LDAP AccessBridge for directory synchronization you must use
                          the Proofpoint Meta account authentication type option. If you do not yet
                          have a Proofpoint Meta account configured please request one from our support team."
                type="warning"
                className={block('Field')}
            />;
        }
        return null;
    };

    renderJIT = (editStore) => {
        if (editStore.isJIT || editStore.isSCIM) {
            return <Fragment>
                <FuseConsoleField
                    className={block('ConsoleField')}
                    contentClassName={block('ConsoleFieldContent')}
                    name="Just In Time (JIT)"
                    info={AuthenticationConfEditStore.JIT_HELP_TXT}
                    infoPosition="right"
                >
                    <FuseSwitch
                        label={editStore.justInTime.isTrue ? 'Enabled' : 'Disabled'}
                        store={editStore.justInTime}
                    />
                </FuseConsoleField>
                <FuseConsoleField
                    className={block('ConsoleField')}
                    contentClassName={block('ConsoleFieldContent')}
                    name="IdP Assume Ownership"
                    info={AuthenticationConfEditStore.ASSUME_OWNERSHIP_HELP_TXT}
                    infoPosition="right"
                >
                    <FuseSwitch
                        label={editStore.idpAssumeOwnership.isTrue ? 'Enabled' : 'Disabled'}
                        store={editStore.idpAssumeOwnership}
                    />
                </FuseConsoleField>
            </Fragment>;
        }
        return null;
    };

    renderIdentityProvider = (editStore) => {
        return <ConsoleCard className={block('Section')}>
            <FTHeading>Identity Provider</FTHeading>
            <FuseTextfield
                store={editStore.idProviderURL}
                label="Identity Provider SSO URL (required)"
                placeholder="Enter identity provider SSO URL"
                className={block('Field', { type: 'text' })}
                error={editStore.idProviderURL.errorMessage !== ''}
                bottomText={editStore.idProviderURL.errorMessage}
            />
            <FuseTextfield
                store={editStore.idProviderIssuer}
                label="Identity Provider Issuer (required)"
                placeholder="Enter identity provider issuer"
                className={block('Field', { type: 'text' })}
            />
            <FuseTextArea
                store={editStore.X_Certificate}
                label="X.509 Certificate (required)"
                placeholder="Paste certificate"
                className={block('Field', { type: 'textarea' })}
                error={editStore.X_Certificate.errorMessage !== ''}
                bottomText={editStore.X_Certificate.errorMessage}
            />
            <ObservableFuseSelect
                store={editStore.contextClass}
                singleSelection
                selectLabel="Authentication Context Class (required)"
                placeholder="Select"
                containerClassName={block('AuthSelect')}
                hintClassName={block('HintOfFuseSelect')}
            />
        </ConsoleCard>;
    };

    renderUrls = (editStore) => {
        return <ConsoleCard className={block('Section')}>
            <FTHeading>SSO URLs</FTHeading>
            <FTBody className={block('UrlsHint')}>
                Copy and paste these into your identity provider.
            </FTBody>
            <div className={block('URLsField')}>
                <FuseTextfield
                    store={editStore.ssoURL}
                    label="SSO URL"
                    placeholder="Enter identity provider issuer"
                    className={block('Field', { type: 'urls' })}
                    disabled
                />
                <FuseButton
                    type={'tertiary'}
                    color={'blue'}
                    icon={'assignment'}
                    className={block('UrlsButton')}
                    onClick={() => this.copyToClipboard(editStore.ssoURL.value, 'SSO URL')}
                > <FTBody className={block('UrlsButtonText')}>Copy to clipboard</FTBody> </FuseButton>
            </div>
            <div className={block('URLsField')}>
                <FuseTextfield
                    store={editStore.recipientURL}
                    label="Recipient URL"
                    placeholder="Enter identity provider issuer"
                    className={block('Field', { type: 'urls' })}
                    disabled
                />
                <FuseButton
                    type={'tertiary'}
                    color={'blue'}
                    icon={'assignment'}
                    className={block('UrlsButton')}
                    onClick={() => this.copyToClipboard(
                        editStore.recipientURL.value, 'Recipient URL')}
                > <FTBody className={block('UrlsButtonText')}>Copy to clipboard</FTBody> </FuseButton>
            </div>
            <div className={block('URLsField')}>
                <FuseTextfield
                    store={editStore.destinationURL}
                    label="Destination URL"
                    placeholder="Enter identity provider issuer"
                    className={block('Field', { type: 'urls' })}
                    disabled
                />
                <FuseButton
                    type={'tertiary'}
                    color={'blue'}
                    icon={'assignment'}
                    className={block('UrlsButton')}
                    onClick={() => this.copyToClipboard(
                        editStore.destinationURL.value, 'Destination URL')}
                > <FTBody className={block('UrlsButtonText')}>Copy to clipboard</FTBody> </FuseButton>
            </div>
            <div className={block('URLsField')}>
                <FuseTextfield
                    store={editStore.audienceURL}
                    label="Audience URI (SP Entity ID)"
                    placeholder="Enter identity provider issuer"
                    className={block('Field', { type: 'urls' })}
                    disabled
                />
                <FuseButton
                    type={'tertiary'}
                    color={'blue'}
                    icon={'assignment'}
                    className={block('UrlsButton')}
                    onClick={() => this.copyToClipboard(
                        editStore.audienceURL.value, 'Audience URI')}
                > <FTBody className={block('UrlsButtonText')}>Copy to clipboard</FTBody> </FuseButton>
            </div>
        </ConsoleCard>;
    };

    renderAuthType = (editStore) => {
        const { authenticationConfiguration } = this.props;
        const hasAccText = <FTBody>
            <b>Proofpoint Meta</b> - we have detected that you have an existing Proofpoint Meta account.
            Please make all configuration changes in the Meta system.
            You can then select the configured IdP from the list for use by Isolation.
        </FTBody>;
        return <FuseConsoleField
            className={block('ConsoleField')}
            contentClassName={block('ConsoleFieldContent')}
            name="Authentication Type"
            info="Configure a new IdP to use with Isolation for authentication and user synchronization or,
                  if you have a Proofpoint Meta account, select an existing IdP configuration to use."
            infoPosition="right"
        >
            {authenticationConfiguration.hasExistingAccount ? hasAccText :
                <Fragment>
                    <FuseRadioButton
                        value={AuthenticationConfEditStore.AUTH_TYPE.IDP}
                        className={block('RadioButton')}
                        store={editStore.authType}
                        label={<span><b>Configure an IdP</b> - configure your own identity provider.</span>}
                    />
                    <FuseRadioButton
                        value={AuthenticationConfEditStore.AUTH_TYPE.META}
                        className={block('RadioButton')}
                        store={editStore.authType}
                        label={<span>
                            <b>Proofpoint Meta</b> - use an existing Proofpoint Meta IdP configuration.
                        </span>}
                    />
                </Fragment>

            }
        </FuseConsoleField>;
    };

    render() {
        const { authenticationConfiguration, useSSOLogin } = this.props;
        const { editStore,
            canDisplayFields,
            createTenantStatus,
            isSSOTenantGotError,
            confSSOTenantErrorMsg } = authenticationConfiguration;
        const portalLink = `https://${editStore.shortName}.metanetworks.com/portal`;

        const displayAdditionalFields = editStore.isConfEnabled.isTrue && editStore.isOwnIDP;

        const provisioningTypeLabel = editStore.isAlreadyConfiguredIDP ? 'reset IdP to change' : 'required';
        const createTenantErrorDetails = `Details: ${confSSOTenantErrorMsg} Please contact support for assistance.`;

        return <FuseConsoleModal
            large
            title="Edit SAML/SSO Configuration"
            isOpen={authenticationConfiguration.editModal.isOpen}
            onClose={authenticationConfiguration.checkShowCancelPrompt}
            greyBackground
            headerActions={<FuseButton
                type={'primary'}
                color={'green'}
                disabled={this.isSaveButtonDisabled()}
                loading={authenticationConfiguration.setConfigurationStatus.isLoading}
                onClick={this.onEditModalSave}
            >Save</FuseButton>}
        >
            <ConsoleCard className={block('Section')}>
                {!editStore.isConfEnabled.isTrue && useSSOLogin ?
                    <FuseAlert
                        type={'warning'}
                        className={block('SSODisabledWarning')}
                        title={'Warning -'}
                        subtitle={'If you disable SAML Status, it will cause the SSO operation to fail for both ' +
                        'SAML/SSO browser registration and SAML/SSO administrator Console login.'}
                    /> : null}
                <div className={block('CardHeader')}>
                    <FTHeading>SAML Authentication</FTHeading>
                </div>
                <FuseConsoleField
                    className={block('ConsoleField')}
                    contentClassName={block('ConsoleFieldContent')}
                    name="SAML Status"
                    info="Enable/Disable SAML SSO support for this organization. Enabling will show
                          an additional SSO user administration menu item."
                    infoPosition="right"
                >
                    <FuseSwitch
                        label={editStore.isConfEnabled.isTrue ? 'Enabled' : 'Disabled'}
                        store={editStore.isConfEnabled}
                        disabled={createTenantStatus.isLoading}
                        onToggleChange={this.onStatusEnable}
                    />
                </FuseConsoleField>
                {createTenantStatus.isLoading ? <UDSpinner type={'fuse'} className={block('Loader')} /> : null}
                {isSSOTenantGotError ? <FuseAlert
                    title={'There was an error connecting to our SAML infrastructure.'}
                    type="danger"
                    className={block('Field')}
                >
                    <FTBody>{createTenantErrorDetails}</FTBody>
                </FuseAlert> : null}
                {canDisplayFields ? this.renderAuthType(editStore) : null}
            </ConsoleCard>
            {canDisplayFields && editStore.isOwnIDP ?
                <ConsoleCard className={block('Section')}>
                    <div className={block('TitleWithButton')}>
                        <FTHeading>Users Provisioning and Group Synchronization</FTHeading>
                        {editStore.isAlreadyConfiguredIDP ? <FuseButton
                            type={'primary'}
                            color={'blue'}
                            onClick={authenticationConfiguration.onResetIDPClick}
                        > Reset IdP </FuseButton> : null}
                    </div>

                    <div className={block('ProvisioningTypeWrapper')}>
                        <ObservableFuseSelect
                            store={editStore.provisioningType}
                            singleSelection
                            selectLabel={`Provisioning Type (${provisioningTypeLabel})`}
                            placeholder="Select"
                            containerClassName={block('ProvisioningType')}
                            hintClassName={block('HintOfFuseSelect')}
                            disabled={editStore.isAlreadyConfiguredIDP}
                        />
                    </div>
                    {this.renderSCIM()}
                    {this.renderJIT(editStore)}
                    {this.renderLDAPWarning(editStore)}
                    {editStore.isSCIM ? <FuseConsoleCardFooter
                        className={block('Footer')}
                        text={'Secret can only be copied to clipboard when a SCIM API Key is first created. ' +
                        'You must delete the current SCIM API Key to get a new secret.'}
                    /> : null}
                </ConsoleCard> : null
            }
            {canDisplayFields && !editStore.isOwnIDP ? <ConsoleCard className={block('Section')}>
                <FTHeading>Proofpoint Meta</FTHeading>
                <ObservableFuseSelect
                    store={editStore.idpConfiguration}
                    singleSelection
                    selectLabel="IdP Configuration (required)"
                    placeholder="Select"
                    containerClassName={block('ProvisioningType')}
                    hintClassName={block('HintOfFuseSelect')}
                />
                <FuseConsoleCardFooter
                    className={block('Footer')}
                    text={<span>
                            Proofpoint Meta IdP configurations are available at{' '}
                        <a href={portalLink} target="_blank" className={block('Link')}>{portalLink}</a>
                    </span>}
                />
            </ConsoleCard> : null
            }
            {displayAdditionalFields ? <Fragment>
                {this.renderIdentityProvider(editStore)}
                {this.renderUrls(editStore)}
            </Fragment> : null}
        </FuseConsoleModal>;
    }
}

export default inject(
    'console',
    'authenticationConfiguration',
    'snack')(observer(EditConfigurationModal));
